import { useRef, useState, type ChangeEvent } from 'react';
import Button from '@/components/base/Button';
import { formatNumber } from '@/lib/format';
import { readFileAsDataUrl } from '@/lib/files';

interface ImageUploadFieldProps {
  id: string;
  label: string;
  name: string;
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  accept: string;
  maxSizeMB: number;
  hint?: string;
  error?: string;
  optionalLabel?: string;
  labels: {
    choose: string;
    replace: string;
    remove: string;
    none: string;
    tooLarge: string;
    wrongType: string;
  };
}

/** Profile-photo picker with preview, replace and remove (data URL in memory). */
export default function ImageUploadField({
  id,
  label,
  name,
  value,
  onChange,
  accept,
  maxSizeMB,
  hint,
  error,
  optionalLabel,
  labels,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const handleSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    setInternalError(null);
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const allowed = accept
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (allowed.length && !allowed.includes(ext)) {
      setInternalError(labels.wrongType);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setInternalError(labels.tooLarge.replace('{size}', formatNumber(maxSizeMB)));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      onChange(dataUrl);
    } catch {
      setInternalError(labels.wrongType);
    }
  };

  const shownError = error || internalError || undefined;

  return (
    <div className="w-full">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-sm font-medium text-foreground-800">{label}</span>
        {optionalLabel ? <span className="text-xs text-foreground-400">({optionalLabel})</span> : null}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-background-300 bg-background-100">
          {value ? (
            <img src={value} alt={label} className="h-full w-full object-cover" />
          ) : (
            <i className="ri-user-line text-3xl text-foreground-400" aria-hidden="true" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="file"
            accept={accept}
            className="sr-only"
            onChange={handleSelect}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon="ri-upload-2-line"
              onClick={() => inputRef.current?.click()}
            >
              {value ? labels.replace : labels.choose}
            </Button>
            {value ? (
              <Button
                variant="danger-outline"
                size="sm"
                icon="ri-delete-bin-line"
                onClick={() => {
                  onChange(null);
                  setInternalError(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                {labels.remove}
              </Button>
            ) : null}
          </div>
          <p className="mt-1.5 text-xs text-foreground-500">
            {shownError ? <span className="text-rose-600">{shownError}</span> : hint}
          </p>
        </div>
      </div>
    </div>
  );
}